import {
  BarChart3, FileText, FolderTree, Image as ImageIcon, LayoutDashboard, LogOut, Megaphone,
  ScrollText, Settings, Users,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Badge, Button } from '../components/ui';

const NAV = [
  { group: 'Editorial', items: [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/articles', label: 'Articles', icon: FileText },
  ] },
  { group: 'Organisation', items: [
    { to: '/admin/categories', label: 'Categories', icon: FolderTree },
    { to: '/admin/media', label: 'Media', icon: ImageIcon },
  ] },
  { group: 'Audience & Ops', items: [
    { to: '/admin/team', label: 'Team & roles', icon: Users },
    { to: '/admin/audit', label: 'Audit log', icon: ScrollText },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/admin/ads', label: 'Advertising', icon: Megaphone },
  ] },
  { group: 'System', items: [
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ] },
];

export function AdminLayout() {
  const { user, roles, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen md:flex">
      <aside className="border-b border-border bg-card md:w-64 md:shrink-0 md:border-b-0 md:border-r">
        <div className="p-5">
          <p className="font-display text-lg font-semibold">The Story Line</p>
          <p className="text-[11px] uppercase tracking-wider text-muted">Content management</p>
        </div>
        <nav aria-label="Admin" className="px-2 pb-6">
          {NAV.map((section) => (
            <div key={section.group} className="mb-4">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted">{section.group}</p>
              <ul>
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm ${isActive ? 'bg-primary/10 font-medium text-primary' : 'text-foreground/80 hover:bg-background'}`
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-4 text-xs">
          {/* The identity the server verified for this session, not a client guess. */}
          <p className="truncate font-medium">{user?.name || user?.email || 'Not signed in'}</p>
          {user?.name && user?.email ? (
            <p className="truncate text-muted" title={user.email}>{user.email}</p>
          ) : null}
          <p className="mt-1 flex flex-wrap gap-1">
            {roles.map((r) => <Badge key={r} tone={r}>{r}</Badge>)}
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>View site</Button>
            <Button variant="ghost" size="sm" onClick={() => { void signOut().then(() => navigate('/admin/login')); }}>
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 bg-background">
        <div className="mx-auto max-w-5xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
